/**
 * AP-31 9.1 — **Länder und Währungen, einmal.**
 *
 * Im Bestand gab es weder eine Währungs- noch eine Länderliste: Die Adressmaske
 * schrieb `country: 'AT'` fest, und `tenant.currency` war die einzige Währung im
 * System. Mit dem ersten Beleg an einen Partner im Ausland braucht beides einen
 * Ort — und zwar **einen**: Drei Apps mit drei Listen wären drei Wahrheiten, und
 * die dritte fiele erst auf, wenn ein Auswahlfeld ein Land kennt, das die
 * Prüfung ablehnt.
 *
 * **Die Liste ist das Auswahlfeld, nicht die Prüfung.** Die API prüft „zwei
 * Großbuchstaben" bzw. „drei Großbuchstaben" — ein Land oder eine Währung
 * außerhalb dieser Liste ist über die Schnittstelle erfassbar, nur eben nicht
 * anklickbar. Eine Stammdatenmaske, die einen Exoten *verhindert*, wird
 * umgangen; eine, die ihn nicht *anbietet*, nicht.
 */

/** Ein Eintrag mit Schlüssel und den beiden gepflegten Sprachen. */
export interface GeoEintrag {
  code: string
  de: string
  en: string
}

/**
 * Die Währungen, die in einem Auswahlfeld stehen — ISO 4217.
 *
 * Kuratiert auf das, was ein Betrieb in Österreich fakturiert: der Euroraum,
 * die Nachbarn mit eigener Währung, die üblichen Handelswährungen.
 */
export const CURRENCIES: readonly GeoEintrag[] = [
  { code: 'EUR', de: 'Euro', en: 'Euro' },
  { code: 'CHF', de: 'Schweizer Franken', en: 'Swiss franc' },
  { code: 'USD', de: 'US-Dollar', en: 'US dollar' },
  { code: 'GBP', de: 'Britisches Pfund', en: 'Pound sterling' },
  { code: 'CZK', de: 'Tschechische Krone', en: 'Czech koruna' },
  { code: 'HUF', de: 'Ungarischer Forint', en: 'Hungarian forint' },
  { code: 'PLN', de: 'Polnischer Złoty', en: 'Polish złoty' },
  { code: 'RON', de: 'Rumänischer Leu', en: 'Romanian leu' },
  { code: 'SEK', de: 'Schwedische Krone', en: 'Swedish krona' },
  { code: 'DKK', de: 'Dänische Krone', en: 'Danish krone' },
  { code: 'NOK', de: 'Norwegische Krone', en: 'Norwegian krone' },
]

export const CURRENCY_CODES: readonly string[] = CURRENCIES.map((w) => w.code)

/**
 * Die Länder, die in einem Auswahlfeld stehen — ISO 3166-1 alpha-2.
 *
 * EU + EFTA + UK + US + TR. Wer weiter liefert, trägt den Code über die
 * Schnittstelle ein oder bekommt hier eine Zeile dazu.
 */
export const COUNTRIES: readonly GeoEintrag[] = [
  { code: 'AT', de: 'Österreich', en: 'Austria' },
  { code: 'BE', de: 'Belgien', en: 'Belgium' },
  { code: 'BG', de: 'Bulgarien', en: 'Bulgaria' },
  { code: 'CH', de: 'Schweiz', en: 'Switzerland' },
  { code: 'CY', de: 'Zypern', en: 'Cyprus' },
  { code: 'CZ', de: 'Tschechien', en: 'Czechia' },
  { code: 'DE', de: 'Deutschland', en: 'Germany' },
  { code: 'DK', de: 'Dänemark', en: 'Denmark' },
  { code: 'EE', de: 'Estland', en: 'Estonia' },
  { code: 'ES', de: 'Spanien', en: 'Spain' },
  { code: 'FI', de: 'Finnland', en: 'Finland' },
  { code: 'FR', de: 'Frankreich', en: 'France' },
  { code: 'GB', de: 'Vereinigtes Königreich', en: 'United Kingdom' },
  { code: 'GR', de: 'Griechenland', en: 'Greece' },
  { code: 'HR', de: 'Kroatien', en: 'Croatia' },
  { code: 'HU', de: 'Ungarn', en: 'Hungary' },
  { code: 'IE', de: 'Irland', en: 'Ireland' },
  { code: 'IS', de: 'Island', en: 'Iceland' },
  { code: 'IT', de: 'Italien', en: 'Italy' },
  { code: 'LI', de: 'Liechtenstein', en: 'Liechtenstein' },
  { code: 'LT', de: 'Litauen', en: 'Lithuania' },
  { code: 'LU', de: 'Luxemburg', en: 'Luxembourg' },
  { code: 'LV', de: 'Lettland', en: 'Latvia' },
  { code: 'MT', de: 'Malta', en: 'Malta' },
  { code: 'NL', de: 'Niederlande', en: 'Netherlands' },
  { code: 'NO', de: 'Norwegen', en: 'Norway' },
  { code: 'PL', de: 'Polen', en: 'Poland' },
  { code: 'PT', de: 'Portugal', en: 'Portugal' },
  { code: 'RO', de: 'Rumänien', en: 'Romania' },
  { code: 'SE', de: 'Schweden', en: 'Sweden' },
  { code: 'SI', de: 'Slowenien', en: 'Slovenia' },
  { code: 'SK', de: 'Slowakei', en: 'Slovakia' },
  { code: 'TR', de: 'Türkei', en: 'Türkiye' },
  { code: 'US', de: 'Vereinigte Staaten', en: 'United States' },
]

export const COUNTRY_CODES: readonly string[] = COUNTRIES.map((l) => l.code)

/**
 * AP-32 7.1 — Die EU-Mitgliedstaaten (ISO-Code, Griechenland also `GR`, nicht
 * das UID-Präfix `EL`). Trägt den **Vorschlag** innergemeinschaftliche Lieferung
 * gegen Ausfuhr; entschieden wird je Beleg.
 */
export const EU_COUNTRY_CODES: readonly string[] = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]

export function landName(code: string, sprache: string | null | undefined): string {
  const eintrag = COUNTRIES.find((l) => l.code === code)
  if (!eintrag) return code
  return sprache === 'en' ? eintrag.en : eintrag.de
}

/** Zwei Großbuchstaben — die Prüfung, die die API anlegt (nicht die Liste). */
export function istLaenderCode(wert: string): boolean {
  return /^[A-Z]{2}$/.test(wert)
}

/** Drei Großbuchstaben — dito für die Währung. */
export function istWaehrungsCode(wert: string): boolean {
  return /^[A-Z]{3}$/.test(wert)
}
